import { useCallback, useMemo } from 'react'

import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { BROADCAST_OPTIONS } from '@ambire-common/libs/broadcast/broadcast'
import { isWeb } from '@common/config/env'
import useNavigation from '@common/hooks/useNavigation'
import { ROUTES } from '@common/modules/router/constants/common'
import { isExtension } from '@web/constants/browserapi'
import useBackgroundService from '@web/hooks/useBackgroundService'

import { getPresentationalStatus } from './helpers'
import { SubmittedAccountOpLike } from './types'

const increaseByFifteenPercent = (value: bigint) => (value * 115n + 99n) / 100n

/**
 * A speed-up rebroadcasts the very same signed txn with a bumped gas price, so
 * everything needed to rebuild it (signer, nonce, gas limit, signature) must be
 * recorded on the submitted op.
 */
export const canBuildSpeedUpAccountOp = (
  submittedAccountOp: SubmittedAccountOpLike
): submittedAccountOp is SubmittedAccountOpLike &
  Required<
    Pick<
      SubmittedAccountOpLike,
      'signingKeyAddr' | 'signingKeyType' | 'nonce' | 'gasLimit' | 'signature'
    >
  > =>
  submittedAccountOp.signingKeyAddr !== undefined &&
  submittedAccountOp.signingKeyType !== undefined &&
  submittedAccountOp.nonce !== undefined &&
  submittedAccountOp.gasLimit !== undefined &&
  submittedAccountOp.signature !== undefined

/**
 * Shared "Speed up" behaviour for a still-pending transaction. Used both by the
 * Activity information sheet footer and by the Activity row itself (so the flow
 * isn't hidden behind opening the sheet).
 */
const useSpeedUpTransaction = ({
  submittedAccountOp,
  closeBottomSheet
}: {
  submittedAccountOp: SubmittedAccountOpLike
  closeBottomSheet?: () => void
}) => {
  const { dispatch } = useBackgroundService()
  const { navigate } = useNavigation()

  const shouldShowSpeedUp = useMemo(() => {
    // Mirror what the detail sheet renders: a MultipleTxns op that is still
    // partially broadcast presents as `PartiallyComplete`, not as pending.
    const status = getPresentationalStatus(submittedAccountOp)
    const isPending =
      status === AccountOpStatus.Pending || status === AccountOpStatus.BroadcastedButNotConfirmed

    return (
      isPending &&
      submittedAccountOp.gasFeePayment?.broadcastOption !== BROADCAST_OPTIONS.byRelayer &&
      submittedAccountOp.gasFeePayment?.broadcastOption !== BROADCAST_OPTIONS.byBundler &&
      canBuildSpeedUpAccountOp(submittedAccountOp)
    )
  }, [submittedAccountOp])

  // On the mobile-web SPA there is no auto-opening action window (the
  // browser-extension background opens one; the SPA has none), so dispatching
  // ADD_CALLS_USER_REQUEST builds the request but nothing navigates and the
  // button looks dead. Mirror PendingRequestsBanner: close the sheet and route
  // to the sign screen ourselves. No-op on the extension, where the background
  // opens the action window.
  const routeToSignOnSpa = useCallback(() => {
    if (!(isWeb && !isExtension)) return
    closeBottomSheet?.()
    navigate(ROUTES.signAccountOp)
  }, [navigate, closeBottomSheet])

  const handleSpeedUpTransaction = useCallback(() => {
    if (!submittedAccountOp.gasFeePayment || !canBuildSpeedUpAccountOp(submittedAccountOp)) return

    const nextGasFeePayment = {
      ...submittedAccountOp.gasFeePayment,
      gasPrice: increaseByFifteenPercent(submittedAccountOp.gasFeePayment.gasPrice),
      maxPriorityFeePerGas:
        submittedAccountOp.gasFeePayment.maxPriorityFeePerGas &&
        submittedAccountOp.gasFeePayment.maxPriorityFeePerGas !== 0n
          ? increaseByFifteenPercent(submittedAccountOp.gasFeePayment.maxPriorityFeePerGas)
          : submittedAccountOp.gasFeePayment.maxPriorityFeePerGas
    }

    dispatch({
      type: 'REQUESTS_CONTROLLER_ADD_CALLS_USER_REQUEST',
      params: {
        // `accountOp` is consumed by ambire-common's RequestsController.build (speed-up
        // path), but ZKX's app-level action type only declares `calls`/`meta`. Cast to
        // forward it without widening the shared action type.
        userRequestParams: {
          calls: submittedAccountOp.calls,
          meta: {
            chainId: submittedAccountOp.chainId,
            accountAddr: submittedAccountOp.accountAddr
          },
          accountOp: {
            id: submittedAccountOp.id,
            accountAddr: submittedAccountOp.accountAddr,
            chainId: submittedAccountOp.chainId,
            signingKeyAddr: submittedAccountOp.signingKeyAddr,
            signingKeyType: submittedAccountOp.signingKeyType,
            nonce: submittedAccountOp.nonce,
            eoaNonce: submittedAccountOp.eoaNonce,
            calls: submittedAccountOp.calls,
            feeCall: submittedAccountOp.feeCall,
            activatorCall: submittedAccountOp.activatorCall,
            gasLimit: submittedAccountOp.gasLimit,
            signature: submittedAccountOp.signature,
            gasFeePayment: nextGasFeePayment,
            txnId: submittedAccountOp.txnId,
            asUserOperation: submittedAccountOp.asUserOperation,
            signers: submittedAccountOp.signers,
            signed: submittedAccountOp.signed,
            safeTx: submittedAccountOp.safeTx,
            flags: submittedAccountOp.flags,
            meta: {
              ...submittedAccountOp.meta,
              speedUp: {
                enabled: true
              }
            }
          }
        } as any
      }
    })
    routeToSignOnSpa()
  }, [submittedAccountOp, dispatch, routeToSignOnSpa])

  return { shouldShowSpeedUp, handleSpeedUpTransaction }
}

export default useSpeedUpTransaction
